import { Injectable } from '@angular/core'
import { NetworkConnectionType, NetworkIpMode } from '../interfaces/network'
import {
  TizenNetworkActiveConnectionType,
  TizenNetworkIpMode,
  TizenSystemInfoVideoSource,
  TizenSystemInfoVideoSourceInfo,
} from './tizen-types'
import { LogService } from 'htv-lib'
import { ExternalInputData, ExternalInputType } from '../services/external-input.service'
import { BehaviorSubject, Observable } from 'rxjs'

declare const b2bapis: any | undefined
declare const tizen: any | undefined
declare const webapis: any | undefined

@Injectable({ providedIn: 'root' })
export class TizenInternal {
  constructor(private logService: LogService) {
    // do nothing
  }

  isTizen(version: number): boolean {
    const userAgent = navigator.userAgent || ''
    const str = 'Tizen' + (version ? ' ' + String(version) : '')
    return userAgent.indexOf(str) === -1 ? false : true
  }

  getCurrentAppInfo(): any {
    try {
      return tizen.application.getAppInfo()
    } catch (err: any) {
      this.logService.error(`[tizen.application.getAppInfo()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getAppInfo(appId: string): any {
    try {
      return tizen.application.getAppInfo(appId)
    } catch (err: any) {
      this.logService.error(`[tizen.application.getAppInfo(${appId})] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getAppVersion(): string {
    try {
      const appInfo = tizen.application.getAppInfo()
      return appInfo.version
    } catch (err: any) {
      this.logService.error(`[tizen.application.getAppInfo().version] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  //
  // webapis.productinfo
  //

  getProductInfoVersion(): string {
    try {
      return webapis.productinfo.getVersion()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getVersion()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getFirmware(): string {
    try {
      return webapis.productinfo.getFirmware()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getFirmware()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getDuid(): string {
    try {
      return webapis.productinfo.getDuid()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getDuid()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getModelCode(): string {
    try {
      return webapis.productinfo.getModelCode()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getModelCode()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getModel(): string {
    try {
      return webapis.productinfo.getModel()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getModel()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getSmartTVServerType(): string {
    try {
      return webapis.productinfo.getSmartTVServerType()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getSmartTVServerType()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getSmartTVServerVersion(): string {
    try {
      return webapis.productinfo.getSmartTVServerVersion()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getSmartTVServerVersion()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  isUdPanelSupported(): string {
    try {
      return webapis.productinfo.isUdPanelSupported()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.isUdPanelSupported()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  is8KPanelSupported(): string {
    try {
      return webapis.productinfo.is8KPanelSupported()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.is8KPanelSupported()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getRealModel(): string {
    try {
      return webapis.productinfo.getRealModel()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getRealModel] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  isWallModel(): string {
    try {
      return webapis.productinfo.isWallModel()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.isWallModel()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getLocalSet(): string {
    try {
      return webapis.productinfo.getLocalSet()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getLocalSet()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  isUHDAModel(): string {
    try {
      return webapis.productinfo.isUHDAModel()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.isUHDAModel()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  isDisplayRotatorSupported(): string {
    try {
      return webapis.productinfo.isDisplayRotatorSupported()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.isDisplayRotatorSupported()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getLicensedVendor(): string {
    try {
      return webapis.productinfo.getLicensedVendor()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getLicensedVendor()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  isOledPanelSupported(): string {
    try {
      return webapis.productinfo.isOledPanelSupported()
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.isOledPanelSupported()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getSystemConfig(key: string): string {
    try {
      return webapis.productinfo.getSystemConfig(key)
    } catch (err: any) {
      this.logService.error(`[webapis.productinfo.getSystemConfig()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  //
  // tizen.systeminfo
  //

  getTotalMemory(): number {
    try {
      return tizen.systeminfo.getTotalMemory()
    } catch (err: any) {
      this.logService.error(`[tizen.systeminfo.getTotalMemory()] [${err?.code}] ${err?.message}`)
      return 0
    }
  }

  getAvailableMemory(): number {
    try {
      return tizen.systeminfo.getAvailableMemory()
    } catch (err: any) {
      this.logService.error(`[tizen.systeminfo.getAvailableMemory()] [${err?.code}] ${err?.message}`)
      return 0
    }
  }

  hasCapability(key: string): boolean {
    let hasCapability = false
    try {
      hasCapability = tizen.systeminfo.getCapability(key)
    } catch (err: any) {
      this.logService.error(`Could not check capability of ${key}, error = ${err?.message}`)
    }
    return hasCapability
  }

  getSystemInfoPropertyValue(propertyId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        tizen.systeminfo.getPropertyValue(
          propertyId,
          (data: any) => {
            resolve(data)
          },
          (err: any) => {
            this.logService.error(`Could not retrieve system info value of ${propertyId}, error = ${err?.message}`)
            reject()
          }
        )
      } catch (err: any) {
        this.logService.error(`Could not retrieve system info property value of ${propertyId}, error = ${err?.message}`)
        reject()
      }
    })
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return new Promise((resolve, reject) => {
      tizen.systeminfo.getPropertyValue(
        'VIDEOSOURCE',
        (videosource: TizenSystemInfoVideoSource) => {
          const connected = videosource.connected.map(vs => this.mapToExternalInputData(vs, true))
          const disconnected = videosource.disconnected.map(vs => this.mapToExternalInputData(vs, false))
          resolve([...connected, ...disconnected])
        },
        (err: any) => {
          this.logService.error(`Could not retrieve external inputs, error: ${err.message}`)
          reject()
        }
      )
    })
  }

  // Disabled since it is causing TV crash when launching the HDMI with the listener registered
  // Disabling the listener before launching the HDMI source did not help with the crash
  // So for Samsung Tizen, we only check the HDMI ports status during startup
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    this.logService.info(`[Tizen] ExternalInput listener disabled for Tizen devices because it is causing TV crash`)
    /*
    const options = {}
    tizen.systeminfo.addPropertyValueChangeListener(
      'VIDEOSOURCE',
      (videosource: TizenSystemInfoVideoSource) => {
        const connected = videosource.connected.map(vs => this.mapToExternalInputData(vs, true))
        const disconnected = videosource.disconnected.map(vs => this.mapToExternalInputData(vs, false))
        subject.next([...connected, ...disconnected])
      },
      options,
      (err: any) => {
        this.logService.error(
          `An error occurred during registering of VIDEOSOURCE change listener, error = ${err.message}`
        )
      }
    )
    */
  }

  private mapToExternalInputData(vs: TizenSystemInfoVideoSourceInfo, connected: boolean): ExternalInputData {
    return {
      id: `${this.getExternalInputType(vs.type)}-${vs.number}`,
      type: this.getExternalInputType(vs.type),
      index: vs.number,
      number: vs.number,
      connected: connected,
    }
  }

  private getExternalInputType(type: string): ExternalInputType {
    switch (type) {
      case 'TV':
        return ExternalInputType.TV
      case 'AV':
        return ExternalInputType.COMPONENT
      case 'SVIDEO':
        return ExternalInputType.SVIDEO
      case 'COMP':
        return ExternalInputType.COMPOSITE
      case 'PC':
        return ExternalInputType.OTHERS
      case 'HDMI':
        return ExternalInputType.HDMI
      case 'SCART':
        return ExternalInputType.SCART
      case 'DVI':
        return ExternalInputType.RGB_DVI
      case 'MEDIA':
        return ExternalInputType.OTHERS
      default:
        return ExternalInputType.OTHERS
    }
  }

  getVideoSource(type: string, portNumber?: number): Promise<TizenSystemInfoVideoSourceInfo> {
    return new Promise((resolve, reject) => {
      tizen.systeminfo.getPropertyValue(
        'VIDEOSOURCE',
        (videoSource: TizenSystemInfoVideoSource) => {
          const connected = videoSource.connected
          let videoSourceInfo: TizenSystemInfoVideoSourceInfo | undefined = undefined
          for (let i = 0; i < connected.length; i++) {
            const connectedVideoSourceInfo = connected[i]
            if (connectedVideoSourceInfo.type !== type) {
              continue
            }
            if (portNumber !== undefined && connectedVideoSourceInfo.number !== portNumber) {
              continue
            }
            videoSourceInfo = connected[i]
            break
          }
          if (videoSourceInfo) {
            resolve(videoSourceInfo)
          } else {
            reject(new Error(`Could not find connected video source for port ${portNumber}`))
          }
        },
        (err: any) => {
          this.logService.error(`Could not retrieve VIDEOSOURCE, error: ${err.message}`)
          reject()
        }
      )
    })
  }

  //
  // tizen.tvwindow
  //

  setTVWindowVideoSource(videoSource: TizenSystemInfoVideoSourceInfo): Promise<void> {
    return new Promise((resolve, reject) => {
      const successCallback = (source: any, type: any) => {
        this.logService.info(
          `Successfully set TV window source, source type: ${source.type}, source port number: ${source.number}, source.signal: ${source.signal}, type: ${type}`
        )
        resolve()
      }
      const errorCallback = (err: any) => {
        this.logService.error(`Could not set source to ${videoSource.number}`)
        this.logService.error(`[${err.code}] ${err.name}: ${err.message}`)
        reject()
      }
      tizen.tvwindow.setSource(videoSource, successCallback, errorCallback)
    })
  }

  showTVWindow(rectangle: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const successCallback = () => {
        resolve()
      }
      const errorCallback = (err: any) => {
        this.logService.error(`Could not show TV window`)
        this.logService.error(`[${err.code}] ${err.name}: ${err.message}`)
        reject()
      }
      try {
        tizen.tvwindow.show(successCallback, errorCallback, rectangle, 'MAIN', 'BEHIND')
      } catch (err: any) {
        this.logService.error(`An error occured during TV window show`)
        this.logService.error(`[${err.code}] ${err.name}: ${err.message}`)
        reject()
      }
    })
  }

  hideTVWindow(): Promise<void> {
    return new Promise((resolve, reject) => {
      const successCallback = () => {
        resolve()
      }
      const errorCallback = (err: any) => {
        this.logService.error(`Could not hide TV window`)
        this.logService.error(`[${err.code}] ${err.name}: ${err.message}`)
        reject()
      }
      try {
        tizen.tvwindow.hide(successCallback, errorCallback, 'MAIN')
      } catch (err: any) {
        this.logService.error(`An error occured during TV window hide`)
        this.logService.error(`[${err.code}] ${err.name}: ${err.message}`)
        reject()
      }
    })
  }

  //
  // webapis.systemcontrol
  //

  getSystemControlVersion(): string {
    try {
      return webapis.systemcontrol.getVersion()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getVersion()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getSerialNumber(): string {
    try {
      return webapis.systemcontrol.getSerialNumber()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getSerialNumber()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getHBrowserSerialNumber(): string {
    try {
      return b2bapis.b2bcontrol.getSerialNumber()
    } catch (err: any) {
      this.logService.error(`[b2bapis.b2bcontrol.getSerialNumber()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getScreenLampSchedule(): string {
    try {
      return webapis.systemcontrol.getScreenLampSchedule()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getScreenLampSchedule()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getPanelMute(): string {
    try {
      return webapis.systemcontrol.getPanelMute()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getPanelMute()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getSafetyLock(): string {
    try {
      return webapis.systemcontrol.getSafetyLock()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getSafetyLock()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getOnScreenMenuOrientation(): string {
    try {
      return webapis.systemcontrol.getOnScreenMenuOrientation()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getOnScreenMenuOrientation()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getSourceOrientation(source: string): string {
    try {
      return webapis.systemcontrol.getSourceOrientation(source)
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getSourceOrientation()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getPCConnection(): string {
    try {
      return webapis.systemcontrol.getPCConnection()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getPCConnection()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getMessageDisplay(): string {
    try {
      return webapis.systemcontrol.getMessageDisplay()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getMessageDisplay()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getCustomAppInfo(): string {
    try {
      return webapis.systemcontrol.getCustomAppInfo()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getCustomAppInfo()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getIRLock(): string {
    try {
      return webapis.systemcontrol.getIRLock()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getIRLock()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getButtonLock(): string {
    try {
      return webapis.systemcontrol.getButtonLock()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getButtonLock()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getAutoPowerOn(): string {
    try {
      return webapis.systemcontrol.getAutoPowerOn()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getAutoPowerOn()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getMagicinfoServerInfo(): string {
    try {
      return webapis.systemcontrol.getMagicinfoServerInfo()
    } catch (err: any) {
      this.logService.error(`[webapis.systemcontrol.getMagicinfoServerInfo()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  //
  // webapis.remotepower
  //

  getRemotePowerVersion(): string {
    try {
      return webapis.remotepower.getVersion()
    } catch (err: any) {
      this.logService.error(`[webapis.remotepower.getVersion()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getPowerState(): string {
    try {
      return webapis.remotepower.getPowerState()
    } catch (err: any) {
      this.logService.error(`[webapis.remotepower.getPowerState()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getVirtualStandbyMode(): string {
    try {
      return webapis.remotepower.getVirtualStandbyMode()
    } catch (err: any) {
      this.logService.error(`[webapis.remotepower.getVirtualStandbyMode()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getRemoteConfiguration(): string {
    try {
      return webapis.remotepower.getRemoteConfiguration()
    } catch (err: any) {
      this.logService.error(`[webapis.remotepower.getRemoteConfiguration()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  //
  // webapis.network
  //

  getNetworkVersion(): string {
    try {
      return webapis.network.getVersion()
    } catch (err: any) {
      this.logService.error(`[webapis.network.getVersion()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  getNetworkConnectionType(): NetworkConnectionType {
    try {
      const activeConnectionType: TizenNetworkActiveConnectionType = webapis.network.getActiveConnectionType()
      switch (activeConnectionType) {
        case TizenNetworkActiveConnectionType.DISCONNECTED:
          return NetworkConnectionType.DISCONNECTED
        case TizenNetworkActiveConnectionType.WIFI:
          return NetworkConnectionType.WIFI
        case TizenNetworkActiveConnectionType.CELLULAR:
          return NetworkConnectionType.CELLULAR
        case TizenNetworkActiveConnectionType.ETHERNET:
          return NetworkConnectionType.ETHERNET
        default:
          return NetworkConnectionType.UNKNOWN
      }
    } catch (err: any) {
      this.logService.error(`Could not retrieve active connection type, error = ${err.message}`)
      return NetworkConnectionType.UNKNOWN
    }
  }

  getNetworkIpMode(): NetworkIpMode {
    try {
      const ipMode: TizenNetworkIpMode = webapis.network.getIpMode()
      switch (ipMode) {
        case TizenNetworkIpMode.NONE:
          return NetworkIpMode.NONE
        case TizenNetworkIpMode.STATIC:
          return NetworkIpMode.STATIC
        case TizenNetworkIpMode.DYNAMIC:
          return NetworkIpMode.DYNAMIC
        case TizenNetworkIpMode.AUTO:
          return NetworkIpMode.AUTO
        case TizenNetworkIpMode.FIXED:
          return NetworkIpMode.FIXED
        case TizenNetworkIpMode.UNKNOWN:
          return NetworkIpMode.UNKNOWN
        default:
          return NetworkIpMode.UNKNOWN
      }
    } catch (err: any) {
      this.logService.error(`Could not retrieve network ipMode, error = ${err.message}`)
      return NetworkIpMode.UNKNOWN
    }
  }

  getNetworkSubnetMask(): string {
    try {
      return webapis.network.getSubnetMask()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network subnet mask, error = ${err.message}`)
      return ''
    }
  }

  getNetworkGateway(): string {
    try {
      return webapis.network.getGateway()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network gateway, error = ${err.message}`)
      return ''
    }
  }

  getNetworkMac(): string {
    try {
      return webapis.network.getMac()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network MAC, error = ${err.message}`)
      return ''
    }
  }

  getNetworkIp(): string {
    try {
      return webapis.network.getIp()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network IP, error = ${err.message}`)
      return ''
    }
  }

  getNetworkDns(): string {
    try {
      return webapis.network.getDns()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network DNS, error = ${err.message}`)
      return ''
    }
  }

  getNetworkSecondaryDns(): string {
    try {
      return webapis.network.getSecondaryDns()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network secondary DNS, error = ${err.message}`)
      return ''
    }
  }

  getNetworkProxy(): string {
    if (this.hasCapability('http://tizen.org/feature/network.net_proxy')) {
      try {
        return webapis.network.getProxyserverInfo()
      } catch (err: any) {
        this.logService.error(`Could not retrieve network Proxy server info, error = ${err.message}`)
      }
    }
    return ''
  }

  getWiFiSsid(): string {
    try {
      return webapis.network.getWiFiSsid()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network wifi ssid, error = ${err.message}`)
      return ''
    }
  }

  getWiFiSignalStrengthLevel(): number {
    try {
      return webapis.network.getWiFiSignalStrengthLevel()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network wifi signal strength level, error = ${err.message}`)
      return 0
    }
  }

  getWiFiSecurityMode(): string {
    try {
      return webapis.network.getWiFiSecurityMode()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network wifi security mode, error = ${err.message}`)
      return ''
    }
  }

  getWiFiEncryptionType(): string {
    try {
      return webapis.network.getWiFiEncryptionType()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network wifi encryption type, error = ${err.message}`)
      return ''
    }
  }

  isSoftAPEnabled(): boolean {
    try {
      return webapis.network.isSoftAPEnabled()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network softAP status, error = ${err.message}`)
      return false
    }
  }

  getSoftAPSSID(): string {
    try {
      return webapis.network.getSoftAPSSID()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network softAP SSID, error = ${err.message}`)
      return ''
    }
  }

  setSoftAPSSID(ssid: string): boolean {
    try {
      webapis.network.setSoftAPSSID(ssid)
      return true
    } catch (err: any) {
      this.logService.error(`Could not set network softAP SSID, error = ${err.message}`)
      return false
    }
  }

  getSoftAPSecurityKey(): string {
    try {
      return webapis.network.getSoftAPSecurityKey()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network softAP SecurityKey, error = ${err.message}`)
      return ''
    }
  }

  getCurrentDhcpOption60Field(): string {
    try {
      return webapis.network.getCurrentDhcpOption60Field()
    } catch (err: any) {
      this.logService.error(`[${err.code}] ${err.message}`)
      return ''
    }
  }

  getTVName(): string {
    try {
      return webapis.network.getTVName()
    } catch (err: any) {
      this.logService.error(`[${err.code}] ${err.message}`)
      return ''
    }
  }

  setTVName(name: string): boolean {
    try {
      webapis.network.setTVName(name)
      return true
    } catch (err: any) {
      this.logService.error(`Could not set TV name, error = ${err.message}`)
      return false
    }
  }

  checkAndUpdateTVName(name: string): boolean {
    const tvName = this.getTVName()
    if (tvName != name) {
      this.logService.info(`Updating TV name to ${name}`)
      return this.setTVName(name)
    } else {
      return false
    }
  }

  //
  // webapis.tvinfo
  //

  getSubtitleState(): boolean {
    try {
      const subtitleState = webapis.tvinfo.getMenuValue(webapis.tvinfo.TvInfoMenuKey.SUBTITLE_ONOFF_KEY)
      return subtitleState == webapis.tvinfo.TvInfoMenuValue.ON
    } catch (err: any) {
      this.logService.error(`Could not retrieve subtitle state, error: ${err.message}`)
      return false
    }
  }

  widevineDrmChallenge(url: string, challenge: string): Observable<string> {
    const message = atob(challenge)
    const messageBuffer = new Uint8Array(message.length)
    for (let i = 0; i < message.length; ++i) {
      messageBuffer[i] = message.charCodeAt(i)
    }

    this.logService.debug(`(widevineDrmChallenge) ${url}`)

    return new Observable(observer => {
      const xhr = new XMLHttpRequest()
      xhr.responseType = 'arraybuffer'

      xhr.open('POST', url, true)

      xhr.onload = () => {
        if (xhr.status === 200 && xhr.response) {
          this.logService.info(`(widevineDrmChallenge) success`)
          try {
            const responseBytes = new Uint8Array(xhr.response)
            const responseLen = responseBytes.byteLength
            const responseList = []
            for (let i = 0; i < responseLen; i++) {
              responseList.push(String.fromCharCode(responseBytes[i]))
            }
            const responseStr = responseList.join('')
            observer.next(responseStr)
            observer.complete()
          } catch (err: any) {
            this.logService.error(`(widevineDrmChallenge) Could not parse DRM license, error = ${err?.message}`)
            observer.error()
          }
        } else {
          this.logService.error(
            `(widevineDrmChallenge) Could not retrieve DRM license, [${xhr.status}] ${xhr.statusText}`
          )
          observer.error()
        }
      }

      xhr.onerror = () => {
        this.logService.error(
          `(widevineDrmChallenge) An error occured during Widevine DRM challenge, error = [${xhr.status}] ${xhr.statusText}`
        )
        observer.error()
      }

      xhr.send(messageBuffer)
    })
  }

  readonly _langs: { [key: string]: string } = {
    afg: 'af',
    alb: 'al',
    dza: 'dz',
    asm: 'as',
    and: 'ad',
    ago: 'ao',
    aia: 'ai',
    ata: 'aq',
    atg: 'ag',
    arg: 'ar',
    arm: 'am',
    abw: 'aw',
    aus: 'au',
    aut: 'at',
    aze: 'az',
    bhs: 'bs',
    bhr: 'bh',
    bgd: 'bd',
    brb: 'bb',
    blr: 'by',
    bel: 'be',
    blz: 'bz',
    ben: 'bj',
    bmu: 'bm',
    btn: 'bt',
    bol: 'bo',
    bes: 'bq',
    bih: 'ba',
    bwa: 'bw',
    bvt: 'bv',
    bra: 'br',
    iot: 'io',
    brn: 'bn',
    bgr: 'bg',
    bfa: 'bf',
    bdi: 'bi',
    cpv: 'cv',
    khm: 'kh',
    cmr: 'cm',
    can: 'ca',
    cym: 'ky',
    caf: 'cf',
    tcd: 'td',
    chl: 'cl',
    chn: 'cn',
    cxr: 'cx',
    cck: 'cc',
    col: 'co',
    com: 'km',
    cod: 'cd',
    cog: 'cg',
    cok: 'ck',
    cri: 'cr',
    hrv: 'hr',
    cub: 'cu',
    cuw: 'cw',
    cyp: 'cy',
    cze: 'cz',
    civ: 'ci',
    dnk: 'dk',
    dji: 'dj',
    dma: 'dm',
    dom: 'do',
    ecu: 'ec',
    egy: 'eg',
    slv: 'sv',
    gnq: 'gq',
    eri: 'er',
    est: 'ee',
    eng: 'en',
    swz: 'sz',
    eth: 'et',
    flk: 'fk',
    fro: 'fo',
    fji: 'fj',
    fin: 'fi',
    fra: 'fr',
    fre: 'fr',
    guf: 'gf',
    pyf: 'pf',
    atf: 'tf',
    gab: 'ga',
    gmb: 'gm',
    geo: 'ge',
    deu: 'de',
    ger: 'de',
    gha: 'gh',
    gib: 'gi',
    grc: 'gr',
    grl: 'gl',
    grd: 'gd',
    glp: 'gp',
    gum: 'gu',
    gtm: 'gt',
    ggy: 'gg',
    gin: 'gn',
    gnb: 'gw',
    guy: 'gy',
    hti: 'ht',
    hmd: 'hm',
    vat: 'va',
    hnd: 'hn',
    hkg: 'hk',
    hun: 'hu',
    isl: 'is',
    ind: 'in',
    idn: 'id',
    irn: 'ir',
    irq: 'iq',
    irl: 'ie',
    imn: 'im',
    isr: 'il',
    ita: 'it',
    jam: 'jm',
    jpn: 'jp',
    jey: 'je',
    jor: 'jo',
    kaz: 'kz',
    ken: 'ke',
    kir: 'ki',
    prk: 'kp',
    kor: 'kr',
    kwt: 'kw',
    kgz: 'kg',
    lao: 'la',
    lva: 'lv',
    lbn: 'lb',
    lso: 'ls',
    lbr: 'lr',
    lby: 'ly',
    lie: 'li',
    ltu: 'lt',
    lux: 'lu',
    mac: 'mo',
    mdg: 'mg',
    mwi: 'mw',
    mys: 'my',
    mdv: 'mv',
    mli: 'ml',
    mlt: 'mt',
    mhl: 'mh',
    mtq: 'mq',
    mrt: 'mr',
    mus: 'mu',
    myt: 'yt',
    mex: 'mx',
    fsm: 'fm',
    mda: 'md',
    mco: 'mc',
    mng: 'mn',
    mne: 'me',
    msr: 'ms',
    mar: 'ma',
    moz: 'mz',
    mmr: 'mm',
    nam: 'na',
    nru: 'nr',
    npl: 'np',
    nld: 'nl',
    ncl: 'nc',
    nzl: 'nz',
    nic: 'ni',
    ner: 'ne',
    nga: 'ng',
    niu: 'nu',
    nfk: 'nf',
    mnp: 'mp',
    nor: 'no',
    omn: 'om',
    pak: 'pk',
    plw: 'pw',
    pse: 'ps',
    pan: 'pa',
    png: 'pg',
    pry: 'py',
    per: 'pe',
    phl: 'ph',
    pcn: 'pn',
    pol: 'pl',
    prt: 'pt',
    pri: 'pr',
    qat: 'qa',
    mkd: 'mk',
    rou: 'ro',
    rus: 'ru',
    rwa: 'rw',
    reu: 're',
    blm: 'bl',
    shn: 'sh',
    kna: 'kn',
    lca: 'lc',
    maf: 'mf',
    spm: 'pm',
    vct: 'vc',
    wsm: 'ws',
    smr: 'sm',
    stp: 'st',
    sau: 'sa',
    sen: 'sn',
    srb: 'rs',
    syc: 'sc',
    sle: 'sl',
    sgp: 'sg',
    sxm: 'sx',
    svk: 'sk',
    svn: 'si',
    slb: 'sb',
    som: 'so',
    zaf: 'za',
    sgs: 'gs',
    ssd: 'ss',
    esp: 'es',
    lka: 'lk',
    sdn: 'sd',
    sur: 'sr',
    sjm: 'sj',
    swe: 'se',
    che: 'ch',
    syr: 'sy',
    twn: 'tw',
    tjk: 'tj',
    tza: 'tz',
    tha: 'th',
    tls: 'tl',
    tgo: 'tg',
    tkl: 'tk',
    ton: 'to',
    tto: 'tt',
    tun: 'tn',
    tur: 'tr',
    tkm: 'tm',
    tca: 'tc',
    tuv: 'tv',
    uga: 'ug',
    ukr: 'ua',
    are: 'ae',
    gbr: 'gb',
    umi: 'um',
    usa: 'us',
    ury: 'uy',
    uzb: 'uz',
    vut: 'vu',
    ven: 've',
    vnm: 'vn',
    vgb: 'vg',
    vir: 'vi',
    wlf: 'wf',
    esh: 'eh',
    yem: 'ye',
    zmb: 'zm',
    zwe: 'zw',
    ala: 'ax',
  }

  getLang(trigram: string): string {
    if (this._langs[trigram] != undefined) {
      return this._langs[trigram]
    } else {
      return trigram
    }
  }
}
